import { ChevronDownIcon, FiltersIcon } from '@arnold/common';
import {
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  useReactTable,
  type ColumnDef,
  type ExpandedState,
  type Row,
  type SortingState,
} from '@tanstack/react-table';
import { Fragment, memo, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type { AnswerGroup } from '../../types';
import { AnswerGroupCell } from './columnDefinitions';
import {
  getColumnDataKey,
  getColumnValue,
  isSearchableColumn,
  normalizeSearchText,
  type ColumnValue,
} from './columnValues';
import { ColumnType, type AnswerGroupTableProps, type TableColumn } from './types';
import {
  ColumnHeading,
  ColumnDragHandle,
  ComparisonBadge,
  ComparisonBanner,
  ComparisonDivider,
  HeaderContent,
  SortButton,
  SearchToolbar,
  SearchField,
  SearchHint,
  SmallButton,
  Disclosure,
  DisclosureSpace,
  EmptyState,
  Footer,
  PageButton,
  ResizeHandle,
  RowIdentity,
  StyledTable,
  TableFrame,
  TableScroller,
} from './styles';
import { useColumnSizing } from './useColumnSizing';
import { orderColumns, useColumnOrdering } from './useColumnOrdering';

const EMPTY_SORTING: SortingState = [];

const columnStyle = (index: number): CSSProperties =>
  ({ '--cell-width': `var(--ag-column-${index})` }) as CSSProperties;

const TableBody = memo(({ rows, columns }: { rows: Row<AnswerGroup>[]; columns: TableColumn[] }) => {
  const { t } = useTranslation('newReport');
  const labelIndex = Math.max(
    0,
    columns.findIndex((column) => column.type === ColumnType.LABEL),
  );
  return (
    <tbody>
      {rows.map((row) => {
        // Only a root carries a comparison role; descendants render as ordinary rows.
        const comparisonRole = row.depth === 0 ? row.original.comparisonRole : undefined;
        return (
          <Fragment key={row.id}>
            {comparisonRole === 'filtered' && (
              <tr data-comparison-divider="true" aria-hidden="true">
                <td colSpan={columns.length}>
                  <ComparisonDivider>
                    <span>{t('table.comparisonVs')}</span>
                  </ComparisonDivider>
                </td>
              </tr>
            )}
            <tr data-comparison-role={comparisonRole}>
              {columns.map((column, index) => (
                <td key={column.id} style={columnStyle(index)}>
                  {index === labelIndex ? (
                    <RowIdentity depth={row.depth}>
                      {row.getCanExpand() ? (
                        <Disclosure
                          type="button"
                          onClick={row.getToggleExpandedHandler()}
                          aria-expanded={row.getIsExpanded()}
                          aria-label={t(row.getIsExpanded() ? 'table.collapseRow' : 'table.expandRow', {
                            label: row.original.label,
                          })}
                        >
                          <ChevronDownIcon
                            width={14}
                            height={14}
                            style={{ transform: row.getIsExpanded() ? undefined : 'rotate(-90deg)' }}
                          />
                        </Disclosure>
                      ) : (
                        <DisclosureSpace />
                      )}
                      <div>
                        <AnswerGroupCell group={row.original} column={column} />
                      </div>
                      {comparisonRole && (
                        <ComparisonBadge role={comparisonRole}>
                          {t(
                            comparisonRole === 'filtered'
                              ? 'table.comparisonFilteredBadge'
                              : 'table.comparisonBaselineBadge',
                          )}
                        </ComparisonBadge>
                      )}
                    </RowIdentity>
                  ) : (
                    <AnswerGroupCell group={row.original} column={column} />
                  )}
                </td>
              ))}
            </tr>
          </Fragment>
        );
      })}
    </tbody>
  );
});

export const AnswerGroupTable = ({
  groups,
  columns: inputColumns,
  pageSize = 10,
  horizontalScroll = false,
  columnResizeEnabled = false,
  sortingEnabled = false,
  searchEnabled = false,
  columnOrderingEnabled = false,
  columnOrder,
  onColumnOrderChange,
  onColumnWidthsChange,
  resetKey,
  label,
}: AnswerGroupTableProps) => {
  const { t, i18n } = useTranslation('newReport');
  const safePageSize = Number.isFinite(pageSize) ? Math.max(1, Math.floor(pageSize)) : 10;
  const [localOrder, setLocalOrder] = useState<string[]>([]);
  const requestedOrder = columnOrder ?? localOrder;
  const columns = useMemo(() => orderColumns(inputColumns, requestedOrder), [inputColumns, requestedOrder]);
  const orderedIds = useMemo(() => columns.map((column) => column.id), [columns]);
  useEffect(() => {
    const validIds = new Set(inputColumns.map((column) => column.id));
    setLocalOrder((previous) => {
      const next = previous.filter((id) => validIds.has(id));
      return next.length === previous.length ? previous : next;
    });
  }, [inputColumns]);
  const sizing = useColumnSizing(columns, horizontalScroll, columnResizeEnabled, onColumnWidthsChange);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [searchExpanded, setSearchExpanded] = useState<ExpandedState>(true);
  const [sorting, setSorting] = useState<SortingState>(EMPTY_SORTING);
  const [query, setQuery] = useState('');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: safePageSize });
  const rootKey = JSON.stringify(groups.map((group) => group.id));
  const columnDataKey = getColumnDataKey(columns);
  // TanStack caches accessors by column ID on each row. Rebinding a column must
  // supply fresh rows; geometry/order/label changes must not rebuild this cache.
  const { data } = useMemo(
    () => ({ data: groups.slice(), columnDataKey, language: i18n.language }),
    [groups, columnDataKey, i18n.language],
  );
  const searchableColumns = useMemo(() => columns.filter(isSearchableColumn), [columns]);
  const hasSearchableColumns = searchableColumns.length > 0;
  const normalizedQuery = normalizeSearchText(query);
  const searchFilter = useMemo(
    () => (searchEnabled && normalizedQuery && hasSearchableColumns ? normalizedQuery : undefined),
    [searchEnabled, normalizedQuery, hasSearchableColumns],
  );
  const hasSortColumn = columns.some((column) => column.id === sorting[0]?.id);
  const activeSorting = sortingEnabled && hasSortColumn ? sorting : EMPTY_SORTING;
  const collator = useMemo(
    () => new Intl.Collator(i18n.language, { numeric: true, sensitivity: 'base' }),
    [i18n.language],
  );
  const definitions = useMemo<ColumnDef<AnswerGroup>[]>(
    () =>
      columns.map<ColumnDef<AnswerGroup>>((column, index) => {
        const bounds = sizing.bounds[index];
        return {
          id: column.id,
          accessorFn: (group) => getColumnValue(group, column),
          size: bounds.width,
          minSize: bounds.minWidth,
          maxSize: bounds.maxWidth,
          enableGlobalFilter: isSearchableColumn(column),
          sortUndefined: false,
          sortingFn: (a, b, id) => {
            const left = a.getValue<ColumnValue>(id);
            const right = b.getValue<ColumnValue>(id);
            // Missing values stay last in either direction, with stable ties.
            if (left === undefined || right === undefined) {
              const missing = left === right ? 0 : left === undefined ? 1 : -1;
              return activeSorting[0]?.desc ? -missing : missing;
            }
            return typeof left === 'number' && typeof right === 'number'
              ? left - right
              : collator.compare(String(left), String(right));
          },
        };
      }),
    [columns, sizing.bounds, activeSorting, collator],
  );
  const table = useReactTable({
    data,
    columns: definitions,
    state: {
      expanded: searchFilter ? searchExpanded : expanded,
      pagination,
      sorting: activeSorting,
      globalFilter: searchFilter,
      columnSizing: sizing.columnSizing,
      columnOrder: orderedIds,
    },
    onColumnSizingChange: sizing.onColumnSizingChange,
    onColumnOrderChange: (updater) => {
      const next = typeof updater === 'function' ? updater(orderedIds) : updater;
      setLocalOrder(next);
      onColumnOrderChange?.(next);
    },
    columnResizeMode: 'onChange',
    onExpandedChange: searchFilter ? setSearchExpanded : setExpanded,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    enableSorting: sortingEnabled,
    enableMultiSort: false,
    sortDescFirst: false,
    getColumnCanGlobalFilter: () => true,
    // A comparison root (baseline vs. filtered) must stay visible even with zero matching descendants,
    // or searching would silently break the side-by-side comparison it's part of.
    globalFilterFn: (row, columnId, filter: string) =>
      (row.depth === 0 && Boolean(row.original.comparisonRole)) ||
      normalizeSearchText(String(row.getValue(columnId) ?? '')).includes(filter),
    filterFromLeafRows: true,
    maxLeafRowFilterDepth: Number.MAX_SAFE_INTEGER,
    getSubRows: (group) => group.children,
    getRowId: (group, _index, parent) => `${parent?.id ?? ''}/${encodeURIComponent(group.id)}`,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    paginateExpandedRows: false,
    autoResetPageIndex: false,
    autoResetExpanded: false,
  });
  const ordering = useColumnOrdering(
    columns,
    columnOrderingEnabled && sizing.resizingId === null,
    sizing.scrollerRef,
    (ids) => {
      // Preserve measured widths by ID, including fit-mode space assigned to the old last column.
      sizing.onColumnSizingChange(sizing.columnSizing);
      table.setColumnOrder(ids);
    },
  );
  useEffect(() => {
    setPagination({ pageIndex: 0, pageSize: safePageSize });
  }, [rootKey, resetKey, safePageSize, activeSorting, searchFilter]);
  useEffect(() => {
    if (!sortingEnabled || !hasSortColumn) setSorting(EMPTY_SORTING);
  }, [sortingEnabled, hasSortColumn]);
  useEffect(() => {
    setSearchExpanded(true);
  }, [searchFilter, resetKey]);
  useEffect(() => {
    setExpanded({});
  }, [resetKey]);
  useEffect(() => {
    const validIds = table.getCoreRowModel().rowsById;
    setExpanded((previous) => {
      if (previous === true) return previous;
      const entries = Object.entries(previous).filter(([id]) => validIds[id]);
      return entries.length === Object.keys(previous).length ? previous : Object.fromEntries(entries);
    });
  }, [groups, table]);
  const filteredRootCount = table.getFilteredRowModel().rows.length;
  const pageCount = Math.ceil(filteredRootCount / safePageSize);
  const pageIndex = Math.min(pagination.pageIndex, Math.max(0, pageCount - 1));
  useEffect(() => {
    // Clamp the latest state, without overwriting a page-zero reset queued above.
    setPagination((previous) => {
      const clamped = Math.min(previous.pageIndex, Math.max(0, pageCount - 1));
      return clamped === previous.pageIndex ? previous : { ...previous, pageIndex: clamped };
    });
  }, [pageCount]);
  const pages = [...new Set([0, pageCount - 1, pageIndex - 1, pageIndex, pageIndex + 1])]
    .filter((page) => page >= 0 && page < pageCount)
    .sort((a, b) => a - b);
  const { widths } = sizing;
  const widthStyles = Object.fromEntries(widths.map((width, index) => [`--ag-column-${index}`, `${width}px`]));
  const tableWidth = widths.reduce((total, width) => total + width, 0);
  const hasComparison = groups.some((group) => group.comparisonRole === 'filtered');

  return (
    <TableFrame data-cy="answer-group-table">
      {hasComparison && (
        <ComparisonBanner>
          <FiltersIcon width={16} height={16} />
          {t('table.comparisonBanner')}
        </ComparisonBanner>
      )}
      {searchEnabled && (
        <SearchToolbar>
          <SearchField>
            {t('table.searchRows')}
            <input
              type="search"
              value={query}
              disabled={!hasSearchableColumns}
              placeholder={t('table.searchPlaceholder')}
              onChange={(event) => setQuery(event.target.value)}
            />
          </SearchField>
          {query && (
            <SmallButton type="button" onClick={() => setQuery('')}>
              {t('table.clearSearch')}
            </SmallButton>
          )}
          <SearchHint>
            {t(hasSearchableColumns ? 'table.searchHint' : 'table.noSearchColumns')}
            {hasSearchableColumns && hasComparison && ` ${t('table.comparisonAlwaysVisibleHint')}`}
          </SearchHint>
        </SearchToolbar>
      )}
      <TableScroller ref={sizing.scrollerRef} scrollEnabled={horizontalScroll}>
        {columns.length > 0 ? (
          <StyledTable aria-label={label} style={{ ...widthStyles, width: tableWidth } as CSSProperties}>
            <colgroup>
              {columns.map((column, index) => (
                <col key={column.id} style={{ width: `var(--ag-column-${index})` }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {columns.map((column, index) => {
                  const tableColumn = table.getColumn(column.id);
                  const direction = tableColumn?.getIsSorted();
                  return (
                    <th
                      key={column.id}
                      scope="col"
                      data-column-id={column.id}
                      data-dragging={ordering.preview?.id === column.id || undefined}
                      data-drop-side={
                        ordering.preview?.target?.id === column.id ? ordering.preview.target.side : undefined
                      }
                      style={columnStyle(index)}
                      aria-sort={
                        sortingEnabled
                          ? direction === 'asc'
                            ? 'ascending'
                            : direction === 'desc'
                              ? 'descending'
                              : 'none'
                          : undefined
                      }
                    >
                      <HeaderContent>
                        {sortingEnabled ? (
                          <SortButton
                            type="button"
                            onClick={tableColumn?.getToggleSortingHandler()}
                            title={t('table.sortColumn', { label: column.label })}
                          >
                            <ColumnHeading as="span">{column.label}</ColumnHeading>
                            <span aria-hidden="true">
                              {direction === 'asc' ? '↑' : direction === 'desc' ? '↓' : '↕'}
                            </span>
                          </SortButton>
                        ) : (
                          <ColumnHeading title={column.label}>{column.label}</ColumnHeading>
                        )}
                        {columnOrderingEnabled && columns.length > 1 && (
                          <ColumnDragHandle
                            type="button"
                            disabled={sizing.resizingId !== null}
                            title={t('table.reorderColumn', { label: column.label })}
                            aria-label={t('table.reorderColumn', { label: column.label })}
                            {...ordering.getDragHandlers(column.id)}
                          >
                            <span aria-hidden="true">⠿</span>
                          </ColumnDragHandle>
                        )}
                      </HeaderContent>
                      {columnResizeEnabled && (horizontalScroll || index < columns.length - 1) && (
                        <ResizeHandle
                          resizing={sizing.resizingId === column.id}
                          title={t('table.resizeColumn', { label: column.label })}
                          {...sizing.getResizeHandlers(index)}
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <TableBody rows={table.getRowModel().rows} columns={columns} />
          </StyledTable>
        ) : (
          <EmptyState>{t('table.noColumns')}</EmptyState>
        )}
        {columns.length > 0 && filteredRootCount === 0 && (
          <EmptyState>{t(searchFilter ? 'table.noSearchResults' : 'table.noRows')}</EmptyState>
        )}
      </TableScroller>
      <Footer>
        <div>
          <PageButton
            type="button"
            disabled={pageIndex === 0}
            onClick={() => table.previousPage()}
            aria-label={t('table.previousPage')}
          >
            ‹
          </PageButton>
          {pages.map((page, index) => (
            <span key={page}>
              {index > 0 && page - pages[index - 1] > 1 && <span> … </span>}
              <PageButton
                type="button"
                active={page === pageIndex}
                aria-current={page === pageIndex ? 'page' : undefined}
                onClick={() => table.setPageIndex(page)}
              >
                {page + 1}
              </PageButton>
            </span>
          ))}
          <PageButton
            type="button"
            disabled={pageCount === 0 || pageIndex >= pageCount - 1}
            onClick={() => table.nextPage()}
            aria-label={t('table.nextPage')}
          >
            ›
          </PageButton>
        </div>
        <span>
          {t('table.rootRange', {
            from: filteredRootCount ? pageIndex * safePageSize + 1 : 0,
            to: Math.min((pageIndex + 1) * safePageSize, filteredRootCount),
            count: filteredRootCount,
          })}
        </span>
      </Footer>
    </TableFrame>
  );
};
